import React, { Component, ReactNode } from 'react';

import { Form, Input } from 'antd';
import { connect } from 'react-redux';
import Icon from '@ant-design/icons';

import {
  IUserSingleSignOnRequest,
  userSingleSignOn,
  userLogout,
  userLogin,
  preLoginCheck,
  repdatatUserSingleSignOn,
  IUserLoginRequest
} from '../../../slices/auth/login.thunks';
import { PrimaryButton } from '../../common/primaryButton';
import { ReactComponent as EyeOffSVG } from '../../../assets/sightx-icons/svg/eye-off.svg';
import { ReactComponent as EyeOnSVG } from '../../../assets/sightx-icons/svg/eye-open.svg';
import { RootState } from '../../../slices/rootReducer';
import { selectors, clearLoginOptions } from '../../../slices/auth';
import { ILoginOptionState } from '../../../slices/auth/login.interfaces';
import { AuthMethod } from '../../../slices/auth/login.thunk.interfaces';
import { FormInstance } from 'antd/lib/form';

import styles from './login.module.scss';

const { selectLoginOptions } = selectors;

interface SingleSignOnFormProps {
  onCancel: () => void;
}

interface IMapStateToProps {
  loginOptions: ILoginOptionState[];
}

interface DispatchProps {
  userSingleSignOn: (request: IUserSingleSignOnRequest) => void;
  preLoginCheck: (request: IUserSingleSignOnRequest) => void;
  userLogout: () => void;
  userLogin: (loginValues: IUserLoginRequest) => void;
  repdatatUserSingleSignOn: (request: IUserSingleSignOnRequest) => void;
  clearLoginOptions: typeof clearLoginOptions;
}

type Props = SingleSignOnFormProps & IMapStateToProps & DispatchProps;

class LoginWithPreCheckFormBase extends Component<Props> {
  private formRef: React.RefObject<FormInstance>;

  public constructor(props: Props) {
    super(props);

    this.formRef = React.createRef<FormInstance>();

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleLoginWithPassword = this.handleLoginWithPassword.bind(this);
  }

  componentDidMount(): void {
    this.props.clearLoginOptions();
  }

  private getHasWorkosSSOLoginOption() {
    const { loginOptions } = this.props;
    return loginOptions.some((loginOption) => loginOption.method === AuthMethod.WorkOSManagedSSO);
  }

  private getHasPasswordLoginOption() {
    const { loginOptions } = this.props;
    return loginOptions.some((loginOption) => loginOption.method === AuthMethod.Password);
  }

  private async handleSubmit() {
    const { loginOptions } = this.props;

    try {
      const values = await this.formRef.current?.validateFields(['email']);
      const { email } = values;

      if(!loginOptions.length) {
        this.props.preLoginCheck({ email });
      }

      const hasWorkosSSOLoginOption = this.getHasWorkosSSOLoginOption();
      if(hasWorkosSSOLoginOption) {
        this.props.repdatatUserSingleSignOn({ email });
      }
    } catch(errorInfo) {
      console.error('SSO login validation failed', errorInfo);
    }
  }

  private async handleLoginWithPassword() {
    try {
      const values = await this.formRef.current?.validateFields(['email', 'password']);
      this.props.userLogin({
        ...values
        // TODO: SIG-20312 add recaptchaToken
      });
    } catch(errorInfo) {
      console.error('Password login validation failed', errorInfo);
    }
  }

  private renderPasswordIcon(visible: boolean) {
    if (visible) {
      return <Icon component={EyeOnSVG} className={styles.passwordIcon} />;
    }
    return <Icon component={EyeOffSVG} className={styles.passwordIcon} />;
  }

  private renderLoginWithPasswordBtn() {
    const hasPasswordLoginOption = this.getHasPasswordLoginOption();

    if (hasPasswordLoginOption) {
      return <PrimaryButton onClick={this.handleLoginWithPassword}>Login with password</PrimaryButton>;
    }

    return null;
  }

  private renderPasswordInputField() {
    const hasPasswordLoginOption = this.getHasPasswordLoginOption();

    if(hasPasswordLoginOption) {
      return (
        <Form.Item
          className={styles.fixedHeight}
          label="Password" name="password"
          rules={[{ required: true }]}
        >
          <Input.Password
            placeholder="∗∗∗∗∗∗∗∗∗∗"
            className={styles.password}
            bordered={false}
            iconRender={this.renderPasswordIcon}
          />
        </Form.Item>
      );
    }

    return null;
  }

  private getSignButtonLabel() {
    const hasPasswordLoginOption = this.getHasPasswordLoginOption();
    const hasWorkosSSOLoginOption = this.getHasWorkosSSOLoginOption();

    if(hasPasswordLoginOption && !hasWorkosSSOLoginOption) {
      return 'Login with password';
    }

    if(hasWorkosSSOLoginOption) {
      return 'Login with SSO';
    }

    return 'Sign In';
  }

  public render(): ReactNode {
    const label = this.getSignButtonLabel();

    return (
      <Form
        className={styles.form}
        ref={this.formRef}
      >
        <Form.Item name="email" rules={[{ type: 'email' }, { required: true }]}>
          <Input placeholder="Email" className={styles.email} bordered={false} />
        </Form.Item>
        {this.renderPasswordInputField()}
        {this.renderLoginWithPasswordBtn()}
        <PrimaryButton onClick={this.handleSubmit}>{label}</PrimaryButton>
      </Form>
    );
  }
}

const mapStateToProps = (state:RootState): IMapStateToProps => {
  return {
    loginOptions: selectLoginOptions(state)
  };
};

const mapDispatchToProps = {
  userSingleSignOn,
  userLogout,
  preLoginCheck,
  clearLoginOptions,
  repdatatUserSingleSignOn,
  userLogin
};

export const LoginWithPreCheckForm = connect(mapStateToProps, mapDispatchToProps)(LoginWithPreCheckFormBase);

